import { onScopeDispose } from "vue";

/**
 * Runs `task` every `interval` ms between `start` and `stop`, and stops for good when the component using it is
 * unmounted.
 *
 * Nothing runs while the tab is hidden. When the tab is shown again, `task` runs straight away and the cycle starts
 * over from there.
 */
export function usePolling(task: () => Promise<void>, interval: number) {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;

  /** The component using it is gone; an await that outlived it can't start a cycle nobody would stop. */
  let disposed = false;

  function clear(): void {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }

  function schedule(): void {
    clear();

    if (running && document.visibilityState === "visible") {
      timer = setTimeout(tick, interval);
    }
  }

  function tick(): void {
    schedule();
    void task();
  }

  function onVisibilityChange(): void {
    if (document.visibilityState === "visible") {
      tick();
    } else {
      clear();
    }
  }

  /** Starts the cycle over, the first run `interval` ms from now. */
  function start(): void {
    if (disposed) return;

    if (!running) document.addEventListener("visibilitychange", onVisibilityChange);

    running = true;
    schedule();
  }

  function stop(): void {
    document.removeEventListener("visibilitychange", onVisibilityChange);
    running = false;
    clear();
  }

  onScopeDispose(() => {
    disposed = true;
    stop();
  });

  return {
    start,
    stop,
  };
}
